import { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { motion } from 'framer-motion';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { ConfigProvider, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { dashboardService, type ManagerFilters } from '../../../../services/dashboardService';
import { CARD_CLASS, EYEBROW_CLASS, OMS_COLORS, SOVEREIGN_CONTROL_THEME, baseChartOptions, sectionVariants, tint } from '../omsDashTheme';
import { toMomentumPoints } from '../utils/deriveTrends';
import type { PerformanceModel } from '../utils/performanceModel';
import { UNMAPPED_LOCATION_ID, UNMAPPED_STATE } from '../utils/performanceModel';
import type { Scope } from '../hooks/useScope';

const { RangePicker } = DatePicker;

type Timeline = 'weekly' | 'monthly' | 'yearly' | 'custom';

const TIMELINE_OPTIONS: Array<{ value: Timeline; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom' },
];

/** Weeks of history to fetch per timeline (backend caps at 104). */
const TIMELINE_WEEKS: Record<Exclude<Timeline, 'custom'>, number> = {
  weekly: 12,
  monthly: 26,
  yearly: 52,
};

function weeksToCover(from: Dayjs): number {
  const weeks = Math.ceil(dayjs().diff(from, 'week', true)) + 1;
  return Math.min(Math.max(weeks, 2), 104);
}

interface Props {
  /** Global scope — the chart follows the Explorer's location/project. */
  scope: Scope;
  model: PerformanceModel;
}

/**
 * Full-width execution momentum board. One area chart, four timelines:
 * weekly (12 wks), monthly (26 wks by month), yearly (52 wks by month) and
 * a custom date range. The trend badge always compares the last two
 * visible points of the selected timeline. The series is scoped
 * server-side to the global scope bar's location/project selection.
 */
export default function MomentumBoard({ scope, model }: Props) {
  const [timeline, setTimeline] = useState<Timeline>('weekly');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);

  const weeks =
    timeline === 'custom'
      ? customRange
        ? weeksToCover(customRange[0])
        : TIMELINE_WEEKS.weekly
      : TIMELINE_WEEKS[timeline];

  // Global scope → server-side filters. The Unmapped bucket has no location
  // ids to filter by, so it falls back to the portfolio-wide series.
  const locationIds = useMemo(() => {
    if (scope.locationId && scope.locationId !== UNMAPPED_LOCATION_ID) return [scope.locationId];
    if (scope.state && scope.state !== UNMAPPED_STATE) return model.locationsOfState.get(scope.state) ?? [];
    return [];
  }, [scope.locationId, scope.state, model.locationsOfState]);
  const projectIds = scope.projectId ? [scope.projectId] : [];
  const scopeCaption = scope.projectId
    ? (model.projects.find((p) => p.id === scope.projectId)?.name ?? 'Selected project')
    : scope.locationId
      ? (model.locations.find((l) => l.id === scope.locationId)?.name ?? 'Selected site')
      : scope.state ?? 'All locations';

  const { data, isLoading } = useQuery({
    queryKey: ['oms-momentum', weeks, locationIds.join(','), projectIds.join(',')],
    queryFn: () => {
      const filters: ManagerFilters & { weeks?: number } = { weeks };
      if (projectIds.length > 0) filters.projectIds = projectIds;
      else if (locationIds.length > 0) filters.locationIds = locationIds;
      return dashboardService.getWeeklyProgress(filters);
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const points = useMemo(() => {
    if (timeline === 'custom') {
      if (!customRange) return [];
      const spanWeeks = customRange[1].diff(customRange[0], 'week', true);
      return toMomentumPoints(data ?? null, {
        groupBy: spanWeeks > 16 ? 'month' : 'week',
        from: customRange[0].startOf('day').toDate(),
        to: customRange[1].endOf('day').toDate(),
      });
    }
    return toMomentumPoints(data ?? null, { groupBy: timeline === 'weekly' ? 'week' : 'month' });
  }, [data, timeline, customRange]);

  const delta = useMemo(() => {
    if (points.length < 2) return null;
    return Math.round(points[points.length - 1].completionRate - points[points.length - 2].completionRate);
  }, [points]);
  const current = points.length > 0 ? points[points.length - 1].completionRate : 0;

  const lastIdx = points.length - 1;

  const chartOptions: ApexOptions = {
    ...baseChartOptions,
    chart: {
      ...baseChartOptions.chart,
      type: 'area',
      stacked: false,
      // Soft shade under the hero line so it lifts off the paper.
      dropShadow: { enabled: true, enabledOnSeries: [0], top: 8, left: 0, blur: 8, color: OMS_COLORS.teal, opacity: 0.16 },
    },
    colors: [OMS_COLORS.mint, OMS_COLORS.amber, OMS_COLORS.slate],
    stroke: { curve: 'smooth', width: [3, 2.25, 1.75], lineCap: 'round' },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: [0.3, 0.16, 0.08], opacityTo: 0, stops: [0, 92, 100] },
    },
    markers: {
      size: 0,
      strokeColors: '#FAF7EE',
      strokeWidth: 2,
      hover: { size: 5 },
      // Endpoint dots on the two headline lines, like a live "now" reading.
      discrete:
        lastIdx >= 0
          ? [
              { seriesIndex: 0, dataPointIndex: lastIdx, size: 5, fillColor: OMS_COLORS.mint, strokeColor: '#FAF7EE', shape: 'circle' },
              { seriesIndex: 1, dataPointIndex: lastIdx, size: 4, fillColor: OMS_COLORS.amber, strokeColor: '#FAF7EE', shape: 'circle' },
            ]
          : [],
    },
    xaxis: {
      categories: points.map((p) => p.label),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: OMS_COLORS.caption, fontSize: '10px', fontWeight: 600 }, hideOverlappingLabels: true, rotate: 0 },
      crosshairs: { show: true, stroke: { color: 'rgba(11,59,46,0.45)', width: 1, dashArray: 5 } },
      tooltip: { enabled: false },
    },
    yaxis: {
      tickAmount: 4,
      min: 0,
      title: { text: 'Tasks per period', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => String(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px', fontWeight: 600 } },
    },
    grid: {
      borderColor: 'rgba(11,59,46,0.08)',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 18, top: 4 },
    },
    legend: {
      ...baseChartOptions.legend,
      position: 'bottom',
      horizontalAlign: 'center',
      itemMargin: { horizontal: 12 },
      markers: { size: 4, strokeWidth: 0 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series, dataPointIndex }: { series: number[][]; dataPointIndex: number }) => {
        const point = points[dataPointIndex];
        if (!point) return '';
        const completed = series[0]?.[dataPointIndex] ?? 0;
        const pending = series[1]?.[dataPointIndex] ?? 0;
        const skipped = series[2]?.[dataPointIndex] ?? 0;
        const prev = dataPointIndex > 0 ? (series[0]?.[dataPointIndex - 1] ?? 0) : null;
        const pct = prev !== null && prev > 0 ? ((completed - prev) / prev) * 100 : null;
        const pill =
          pct !== null
            ? `<span style="font-size:10px;font-weight:800;padding:2px 7px;border-radius:999px;font-variant-numeric:tabular-nums;${
                pct >= 0
                  ? 'color:#8FDDBC;background:rgba(51,146,116,0.28);'
                  : 'color:#F3B3C2;background:rgba(159,18,57,0.34);'
              }">${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%</span>`
            : '';
        const row = (dot: string, label: string, value: number) =>
          `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;font-size:10px;">
             <span style="display:flex;align-items:center;gap:5px;color:rgba(242,234,219,0.72);font-weight:600;">
               <i style="width:6px;height:6px;border-radius:999px;background:${dot};display:inline-block;"></i>${label}
             </span>
             <span style="color:#F8F2E2;font-weight:700;font-variant-numeric:tabular-nums;">${value}</span>
           </div>`;
        return `<div style="background:#0B3B2E;border:1px solid rgba(201,169,97,0.38);border-radius:12px;padding:10px 13px;min-width:158px;box-shadow:0 14px 30px -12px rgba(11,59,46,0.55);font-family:inherit;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-size:16px;font-weight:800;color:#F8F2E2;font-variant-numeric:tabular-nums;line-height:1;">${completed} done</span>
              ${pill}
            </div>
            <div style="margin-top:4px;font-size:10px;font-weight:600;letter-spacing:0.05em;color:rgba(242,234,219,0.6);">${point.label}</div>
            <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(242,234,219,0.14);display:flex;flex-direction:column;gap:3px;">
              ${row(OMS_COLORS.amber, 'Pending', pending)}
              ${row(OMS_COLORS.slate, 'Skipped', skipped)}
            </div>
          </div>`;
      },
    },
  };

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={5}
      aria-label="Execution momentum"
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <p className={EYEBROW_CLASS}>
            Momentum · execution trend ·{' '}
            <span style={{ color: OMS_COLORS.champagneDeep }}>{scopeCaption}</span>
          </p>
          {delta !== null && (
            <span
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold tabular-nums"
              style={
                delta >= 0
                  ? { color: OMS_COLORS.teal, backgroundColor: tint(OMS_COLORS.mint, 0.14) }
                  : { color: OMS_COLORS.red, backgroundColor: tint(OMS_COLORS.red, 0.10) }
              }
              title="Change in completion rate vs the previous period"
            >
              {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {delta >= 0 ? '+' : ''}{delta} pts vs previous period · now {current}% complete
            </span>
          )}
        </div>

        <ConfigProvider theme={SOVEREIGN_CONTROL_THEME}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl border border-[rgba(11,59,46,0.24)] bg-[#fcfaf3] p-0.5">
              {TIMELINE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTimeline(opt.value)}
                  aria-pressed={timeline === opt.value}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                    timeline === opt.value ? 'bg-[#1F5A47] text-[#F2EADB] shadow-sm' : 'text-[#5A6275] hover:text-[#1F5A47]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {timeline === 'custom' && (
              <RangePicker
                size="small"
                value={customRange}
                onChange={(range) => {
                  if (range?.[0] && range?.[1]) setCustomRange([range[0], range[1]]);
                  else setCustomRange(null);
                }}
                disabledDate={(d) => d.isAfter(dayjs().endOf('day'))}
                allowClear
              />
            )}
          </div>
        </ConfigProvider>
      </div>

      {isLoading && !data ? (
        <div className="mt-3 h-72 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
      ) : points.length === 0 ? (
        <div className="flex h-72 items-center justify-center text-sm font-medium" style={{ color: OMS_COLORS.caption }}>
          {timeline === 'custom' && !customRange
            ? 'Pick a date range to plot the trend'
            : scope.state || scope.projectId
              ? `No progress data for ${scopeCaption} in this timeline`
              : 'No progress data for this timeline'}
        </div>
      ) : (
        <>
          <Chart
            type="area"
            height={300}
            options={chartOptions}
            series={[
              { name: 'Completed', data: points.map((p) => p.completed) },
              { name: 'Pending', data: points.map((p) => p.pending) },
              { name: 'Skipped', data: points.map((p) => p.skipped) },
            ]}
          />
          <p className="px-1 text-[10px] font-medium" style={{ color: OMS_COLORS.caption }}>
            Completion rate = tasks decided (pass or fail) ÷ tasks scheduled in each period. Skipped = auto-superseded by the next same-frequency task.
          </p>
        </>
      )}
    </motion.section>
  );
}
